import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, CreditCard, Loader2, ExternalLink, QrCode, Upload } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const CheckoutPage = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("moncash");
  const [proofFile, setProofFile] = useState(null);
  const [formData, setFormData] = useState({
    phone: "",
    email: ""
  });

  useEffect(() => {
    fetchCard();
  }, [cardId]);

  // Check if returning from MonCash payment
  useEffect(() => {
    const transactionId = searchParams.get('transactionId');
    if (transactionId) {
      // Handle MonCash callback
      handleMoncashReturn(transactionId);
    }
  }, [searchParams]);

  const fetchCard = async () => {
    try {
      const response = await axios.get(`${API}/gift-cards`);
      const foundCard = response.data.find(c => c.id === cardId && !c.is_sold);
      if (foundCard) {
        setCard(foundCard);
      } else {
        toast.error("Kat kado sa a pa disponib ankò");
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching card:", error);
      toast.error("Erè pandan chajman");
    } finally {
      setLoading(false);
    }
  };

  const handleMoncashReturn = async (transactionId) => {
    try {
      const response = await axios.get(`${API}/moncash/callback?transactionId=${transactionId}`);
      if (response.data.success && response.data.order_id) {
        toast.success("Peman reyisi!");
        navigate(`/order/${response.data.order_id}`);
      }
    } catch (error) {
      console.error("Error handling MonCash return:", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-HT', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone || formData.phone.length < 8) {
      toast.error("Tanpri antre yon nimewo telefòn valid");
      return;
    }

    if (paymentMethod === "direct" && !proofFile) {
      toast.error("Tanpri soumèt prèv peman an pou Depo Direct la");
      return;
    }

    setProcessing(true);

    try {
      // Step 1: Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        gift_card_id: cardId,
        customer_phone: formData.phone,
        customer_email: formData.email || null,
        payment_method: paymentMethod
      });

      const order = orderResponse.data;

      if (paymentMethod === "moncash") {
        // Step 2: Create MonCash payment and get redirect URL
        const moncashResponse = await axios.post(`${API}/moncash/create-payment?order_id=${order.id}`);
        
        if (moncashResponse.data.redirect_url) {
          localStorage.setItem('pending_order_id', order.id);
          toast.success("Ap redirije ou sou Moncash...");
          window.location.href = moncashResponse.data.redirect_url;
        } else {
          throw new Error("Pa kapab kreye peman Moncash");
        }
      } else {
        // Step 2 for Direct: Upload proof
        if (proofFile) {
          const fileData = new FormData();
          fileData.append("file", proofFile);
          await axios.post(`${API}/orders/${order.id}/proof`, fileData);
        }
        toast.success("Kòmand ou an anrejistre avèk siksè!");
        navigate(`/order/${order.id}`);
      }

    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(error.response?.data?.detail || "Erè pandan peman an");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="loading-spinner" data-testid="loading-spinner"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="checkout-page">
      <Header />

      <main className="flex-1 py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mb-6 text-gray-600 hover:text-[#232F3E]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retounen
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Order Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6 h-fit" data-testid="order-summary">
              <h2 className="text-2xl font-bold text-[#232F3E] mb-6">Rezime Kòmand</h2>
              
              {/* Gift Card Preview */}
              <div className="gift-card-visual rounded-xl p-6 mb-6">
                <div className="flex justify-between items-start mb-8">
                  <div className="text-white">
                    <span className="text-xs uppercase tracking-widest opacity-70">Amazon</span>
                    <p className="text-xl font-bold amazon-text">Gift Card</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#FF9900] flex items-center justify-center">
                    <span className="text-black font-bold">$</span>
                  </div>
                </div>
                <div className="amount-badge inline-block text-2xl">
                  ${card?.denomination}
                </div>
              </div>

              {/* Price Details */}
              <div className="space-y-3 border-t border-gray-100 pt-4">
                <div className="flex justify-between text-gray-600">
                  <span>Valè Kat Kado</span>
                  <span>${card?.denomination} USD</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-[#232F3E]">
                  <span>Total</span>
                  <span className="text-[#FF9900]">{formatPrice(card?.price_htg)} HTG</span>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="payment-form">
              <h2 className="text-2xl font-bold text-[#232F3E] mb-4">Enfòmasyon Peman</h2>

              {/* Payment Method Toggle */}
              <div className="flex gap-4 mb-6">
                <Button 
                  type="button" 
                  variant={paymentMethod === 'moncash' ? 'default' : 'outline'} 
                  className={paymentMethod === 'moncash' ? 'bg-[#FF9900] hover:bg-[#F08C00] text-black flex-1 h-12' : 'flex-1 h-12'} 
                  onClick={() => setPaymentMethod('moncash')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  MonCash
                </Button>
                <Button 
                  type="button" 
                  variant={paymentMethod === 'direct' ? 'default' : 'outline'} 
                  className={paymentMethod === 'direct' ? 'bg-[#FF9900] hover:bg-[#F08C00] text-black flex-1 h-12' : 'flex-1 h-12'} 
                  onClick={() => setPaymentMethod('direct')}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Depo Direct
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Phone Number */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-medium">
                    Nimewo Telefòn Moncash *
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+509 XXXX-XXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="pl-10 h-12"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Nimewo sa a pral resevwa notifikasyon peman an
                  </p>
                </div>

                {/* Email (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    Imèl (Opsyonèl)
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="ou@egzanp.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="pl-10 h-12"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {paymentMethod === "direct" && (
                  <div className="bg-blue-50/50 p-4 border border-blue-100 rounded-xl space-y-4">
                    <div className="text-center">
                      <h3 className="font-bold text-[#232F3E] mb-2">Fè Depo nan MonCash</h3>
                      <p className="text-sm text-gray-600 mb-4">Eskane kòd sa oswa voye {formatPrice(card?.price_htg)} HTG sou nimewo <strong>37759711 (BVG PROMO HAITI)</strong></p>
                      <img src="/moncash_qr.jpg" alt="MonCash QR" className="w-48 h-auto mx-auto border-4 border-white shadow-sm rounded-xl mb-4" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="proof" className="text-gray-700 font-medium flex items-center gap-2">
                        <Upload className="w-4 h-4 text-[#FF9900]" />
                        Voye Prèv Peman an *
                      </Label>
                      <Input
                        id="proof"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setProofFile(e.target.files[0])}
                        className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#FF9900]/10 file:text-[#FF9900] hover:file:bg-[#FF9900]/20 h-14"
                        required={paymentMethod === "direct"}
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={processing}
                    style={{
                      backgroundColor: paymentMethod === 'moncash' ? '#E31C23' : '#FF9900',
                      color: paymentMethod === 'moncash' ? 'white' : 'black',
                      width: '100%',
                      padding: '1.5rem',
                      fontSize: '1.125rem',
                      fontWeight: 'bold',
                      borderRadius: '0.75rem',
                      border: 'none',
                      cursor: processing ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: processing ? 0.8 : 1,
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => !processing && (e.target.style.backgroundColor = paymentMethod === 'moncash' ? '#C41920' : '#F08C00')}
                    onMouseOut={(e) => !processing && (e.target.style.backgroundColor = paymentMethod === 'moncash' ? '#E31C23' : '#FF9900')}
                    data-testid="pay-button"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Ap trete...
                      </>
                    ) : (
                      <>
                        {paymentMethod === 'moncash' ? (
                          <><CreditCard className="w-5 h-5 mr-2" /> Peye ak Moncash</>
                        ) : (
                          <><Upload className="w-5 h-5 mr-2" /> Voye Kòmand la</>
                        )}
                        {" - "}{formatPrice(card?.price_htg)} HTG
                        {paymentMethod === 'moncash' && <ExternalLink className="w-4 h-4 ml-2" />}
                      </>
                    )}
                  </button>
                </div>

                {/* Security Note */}
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">
                    🔒 Ou pral redirije sou paj Moncash pou konplete peman an. 
                    Apre peman, ou pral retounen isit la pou wè kòd kat kado ou.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

